import React, { useEffect } from 'react';
import NavRail, { type NavItem } from './NavRail';
import GroupedNavRail from './GroupedNavRail';
import { navigateFromRail } from './useResourceRoute';
import { PurposesDialogHost } from '../modules/purposes/PurposesDialog';
import { usePermSync } from './usePermSync';
import { NotificationBell } from './NotificationBell';
import NexusAssistant from './NexusAssistant';
import AssistantPanel from './AssistantPanel';
import { CommandPalette } from './CommandPalette';
import { ObjectContextPanel } from './ObjectContextPanel';
import { Breadcrumb } from '../design-system/components/Breadcrumb';
import TourOverlay from '../tour/TourOverlay';
import { ConnectOnboarding } from './ConnectOnboarding';
import { ThemeToggle } from '../design-system/components/ThemeToggle';
import { useNavigationStore } from '../store/navigationStore';
import { useGlobalKeyboard } from '../hooks/useGlobalKeyboard';
import { useAuthStore, endImpersonation, IMP_EXPIRED_KEY } from '../store/authStore';
import KitchenReturnBar from './KitchenReturnBar';
import { usePermission } from '../hooks/usePermission';
import { BranchShell } from './BranchSwitcher';
import { permissionModuleFor } from './routes';
import { PageErrorBoundary } from './PageErrorBoundary';
import { useUiV2 } from './uiFlags';
import { useAppFocusStore } from './appFocus';
import { ResourceUrlSync } from './ResourceUrlSync';
import { BillingNoticeHost } from './billingNotice/BillingNoticeBanner';

import { useTranslation } from 'react-i18next';

interface AppShellProps {
  children: (page: string) => React.ReactNode;
  additionalNavigation?: NavItem[];
  workspaceBar?: React.ReactNode;
}

/** Shown once after an impersonation ended because it could not be renewed —
 *  the user is back in their own session and needs to know before they keep
 *  working, since anything they do now happens in their own tenant. */
const ImpersonationEndedNotice: React.FC = () => {
  const [expired, setExpired] = React.useState(() => !!sessionStorage.getItem(IMP_EXPIRED_KEY));
  if (!expired) return null;
  const dismiss = () => {
    sessionStorage.removeItem(IMP_EXPIRED_KEY);
    setExpired(false);
  };
  return (
    <div style={{
      backgroundColor: '#F59E0B', color: '#78350F', padding: '6px 16px',
      fontSize: 12, fontWeight: 600, display: 'flex',
      alignItems: 'center', justifyContent: 'center', gap: 12,
      borderBottom: '2px solid #B45309',
    }}>
      <span>
        The impersonated session ended and could not be renewed — you are back in
        your own account and tenant.
      </span>
      <button
        onClick={dismiss}
        style={{
          background: '#78350F', border: 'none', color: '#fff',
          padding: '3px 14px', borderRadius: 4, fontSize: 11,
          cursor: 'pointer', fontWeight: 700,
        }}
      >
        Dismiss
      </button>
    </div>
  );
};

const ImpersonationBanner: React.FC = () => {
  const user = useAuthStore(s => s.user);
  const { t } = useTranslation();
  if (!user?.impersonated_by) return null;

  const exitImpersonation = () => endImpersonation();

  return (
    <>
      <style>{`@keyframes impersonatePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }`}</style>
      <div style={{
        backgroundColor: '#DC2626', color: '#fff', padding: '6px 16px',
        fontSize: 12, display: 'flex',
        alignItems: 'center', justifyContent: 'center', gap: 12,
        fontWeight: 500, animation: 'impersonatePulse 2s ease-in-out infinite',
        borderBottom: '2px solid #991B1B',
      }}>
        <span style={{
          backgroundColor: '#fff', color: '#DC2626', padding: '1px 8px',
          borderRadius: 10, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
        }}>
          {t('user.impersonating', 'IMPERSONATING')}
        </span>
        <span>{user.name} ({user.email}) in {user.tenant_id}</span>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>
          {t('user.by', 'by')} {user.impersonated_by}
        </span>
        <button
          onClick={exitImpersonation}
          style={{
            background: '#fff', border: 'none',
            color: '#DC2626', padding: '3px 14px', borderRadius: 4, fontSize: 11,
            cursor: 'pointer', fontWeight: 700, marginLeft: 8,
          }}
        >
          {t('user.exitImpersonation', 'Exit Impersonation')}
        </button>
      </div>
    </>
  );
};

export const AppShell: React.FC<AppShellProps> = ({ children, additionalNavigation, workspaceBar }) => {
  const { currentPage, redirectTo, syncFromLocation } = useNavigationStore();
  const { effective, canAccess } = usePermission();
  // INTRO-17: read the permission document again when an admin changes it.
  usePermSync(useAuthStore(s => s.user?.id));
  // New interface (ui_v2, per tenant): grouped sidebar with the help menu.
  // URLs, the record page and Home work in both.
  const uiV2 = useUiV2();
  // An operator app in focused mode (C5, REDISENO_UI.md 3.34) takes the whole
  // window: no top bar or project bar; the app keeps its own exit button.
  const appFocus = useAppFocusStore((s) => s.focused) && uiV2;

  // Browser back/forward: the URL changed under us, so re-read it.
  useEffect(() => {
    window.addEventListener('popstate', syncFromLocation);
    return () => window.removeEventListener('popstate', syncFromLocation);
  }, [syncFromLocation]);

  // Mount global keyboard handler
  useGlobalKeyboard();

  // A profile-constrained user defaults to (or gets stranded on) a page they
  // can't access — e.g. Dashboards when only App Studio is granted. Redirect
  // them to their first granted module.
  //
  // The `effective.full` early-return this used to open with was the bug: it
  // meant "unconstrained users are untouched", but Phase 2 of the RBAC
  // migration made `full` false for EVERY user, so the guard started applying
  // to everyone — and it redirected any page a profile cannot grant, which is
  // nine of them. `effective.modules` is sorted, so they all landed on
  // `activity`. The fix is not to restore the early return (a real profile
  // constraint must still redirect) but to only judge pages the permission
  // system actually gates; `moduleEnabled` now answers true for the rest.
  useEffect(() => {
    if (!effective) return;
    // 'apps' (Dashboards) and 'apps-app' (Apps) are separate gateable modules;
    // gate the page by its own module id, or by the module it belongs to
    // (a record by Ontology, Process Mining by Activity).
    if (canAccess(permissionModuleFor(currentPage))) return;
    const first = (effective.modules || [])[0];
    // Replace, not push: Back must not return to a page the user cannot open.
    if (first && first !== currentPage) redirectTo(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effective, currentPage]);

  const mainEl = (
    <main style={{
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    }}>
      <PageErrorBoundary key={currentPage}>{children(currentPage)}</PageErrorBoundary>
    </main>
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
      backgroundColor: 'var(--color-base)',
    }}>
      {/* Overdue-payment notice set by a platform operator. In the flow, so it
          pushes the app down; it never blocks using the platform. */}
      <BillingNoticeHost />
      <ImpersonationEndedNotice />
      <ImpersonationBanner />
      <KitchenReturnBar />
      {/* Branch switcher: permanent "you are in a branch" banner + dialogs. */}
      <BranchShell />
      {/* Propósitos de acceso (INTRO-6): el menú de usuario lo abre. */}
      <PurposesDialogHost />
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      {uiV2
        ? <GroupedNavRail currentPage={currentPage} onNavigate={navigateFromRail} additionalItems={additionalNavigation} />
        : <NavRail currentPage={currentPage} onNavigate={navigateFromRail} additionalItems={additionalNavigation} />}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Breadcrumb bar */}
        {!appFocus && <Breadcrumb />}
        {!appFocus && workspaceBar}

        {uiV2 ? (
          // The single assistant (C18) docks next to the page, under the top
          // bars, so opening it narrows the page and not the breadcrumbs.
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
            {mainEl}
            <AssistantPanel />
          </div>
        ) : mainEl}
      </div>

      {/* Nexus Assistant — right-side panel (with ui_v2, docked above). */}
      {!uiV2 && <NexusAssistant />}

      {/* Object context panel */}
      <ObjectContextPanel />

      {/* Global command palette — Cmd+K */}
      <CommandPalette />

      {/* Guided clickthrough demo */}
      <TourOverlay />

      {/* First-run "connect your data" guide for a tenant with no sources. */}
      {!appFocus && <ConnectOnboarding />}

      {/* URL per resource for pages that keep their selection in a store (B1). */}
      <ResourceUrlSync />

    </div>
    </div>
  );
};

export default AppShell;
